import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { PhotoCamera, Upload } from "@mui/icons-material";
import axios from "axios";
import "./CropClinic.css";
import BASE_URL from "../../../config/apiConfig";

const CropClinic = () => {
  const { crop_name } = useParams();
  const location = useLocation();
  const { id, crop_clinic, cropname } = location.state || {};
  const [cropName] = useState(crop_name);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disease, setDisease] = useState(null);
  const [precautions, setPrecautions] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const fetchRecommendedProducts = async (diseaseName, cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/recommended-products`,
        {
          diseaseName,
          cropName,
        },
      );
      setRecommendedProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching recommended products:", error);
    }
  };

  useEffect(() => {
    if (disease && cropName) {
      fetchRecommendedProducts(disease, cropName);
    }
  }, [disease]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setOpen(false);
      setDisease(null);
      setPrecautions(null);
    }
  };

  const handleDiagnose = async () => {
    if (!selectedImage) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("image", selectedImage);
    formData.append("crop_name", cropname);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/identify`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const diseaseName = response.data.diseaseName;
      const imageName = response.data.imageName;

      setDisease(diseaseName);

      const precautionsResponse = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/precautions`,
        {
          diseaseName,
          id,
          crop_clinic,
          cropname,
          imageName,
        },
      );

      setPrecautions(precautionsResponse.data.precautions);
    } catch (error) {
      console.error("Error:", error);
      setDisease("Error identifying disease.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="farmology-clinic-page">
      <section className="clinic-page-hero">
        <div className="clinic-hero-overlay" />
        <div className="clinic-hero-grid" />

        <div className="clinic-hero-content">
          <span className="clinic-hero-eyebrow">FARMOLOGY PLATFORM</span>

          <div className="clinic-hero-title-row">
            <div>
              <h1>Crop Clinic</h1>
              <p>
                Diagnose crop problems from a photo and get practical,
                crop-specific guidance to help protect your plants.
              </p>
            </div>

            <div className="clinic-hero-meta">
              <span>CROP HEALTH</span>
              <strong>{cropname || cropName || "CROP"}</strong>
            </div>
          </div>
        </div>

        <div className="clinic-hero-bottom">
          <span>AI-ASSISTED CROP DIAGNOSIS</span>
          <span>PHOTO → DIAGNOSIS → ACTION</span>
        </div>
      </section>

      <main className="clinic-page-content">
        <section className="clinic-intro-section">
          <div className="clinic-section-heading">
            <span className="clinic-eyebrow">CROP DIAGNOSIS</span>
            <h2>Check your crop health</h2>
            <p>
              Upload a clear image of the affected crop area. Farmology will
              analyze the image and provide a diagnosis with practical
              precautions.
            </p>
          </div>

          <div className="clinic-workspace">
            <div className="clinic-upload-card">
              <div className="clinic-card-header">
                <div>
                  <span className="clinic-card-kicker">STEP 01</span>
                  <h3>Upload a crop photo</h3>
                  <p>
                    Choose a clear, well-lit photo of the affected leaves, stem
                    or fruit.
                  </p>
                </div>
                <div className="clinic-card-icon">
                  <PhotoCamera />
                </div>
              </div>

              <div className="clinic-crop-field">
                <TextField
                  label="Crop Name"
                  value={cropName}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  fullWidth
                  className="clinic-crop-input"
                />
              </div>

              <div
                className={`clinic-photo-dropzone ${selectedImage ? "has-image" : ""}`}
              >
                {selectedImage ? (
                  <div className="clinic-preview-wrap">
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="Selected crop"
                      className="clinic-preview-image"
                    />
                    <div className="clinic-preview-overlay">
                      <span>Photo selected</span>
                      <button type="button" onClick={handleOpen}>
                        Change photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="clinic-upload-placeholder">
                    <div className="clinic-upload-icon">
                      <Upload />
                    </div>
                    <strong>Upload or take a photo</strong>
                    <span>JPG, PNG or other image formats · Max 5 MB</span>
                    <Button
                      variant="contained"
                      onClick={handleOpen}
                      startIcon={<Upload />}
                      className="clinic-primary-button"
                    >
                      Choose Photo
                    </Button>
                  </div>
                )}
              </div>

              {selectedImage && (
                <Button
                  variant="outlined"
                  onClick={handleOpen}
                  startIcon={<Upload />}
                  className="clinic-secondary-button"
                >
                  Replace Photo
                </Button>
              )}

              <Button
                variant="contained"
                onClick={handleDiagnose}
                className="clinic-diagnose-button"
                disabled={!selectedImage || loading}
                fullWidth
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" />
                    <span>Analyzing crop...</span>
                  </>
                ) : (
                  "Diagnose Your Crop"
                )}
              </Button>
            </div>

            <div className="clinic-guidance-card">
              <div className="clinic-card-header compact">
                <div>
                  <span className="clinic-card-kicker">PHOTO GUIDE</span>
                  <h3>Get better results</h3>
                </div>
              </div>

              <div className="clinic-instructions">
                <div className="clinic-instruction-item">
                  <span>01</span>
                  <div>
                    <strong>Use good lighting</strong>
                    <p>Take the photo in clear, natural light.</p>
                  </div>
                </div>

                <div className="clinic-instruction-item">
                  <span>02</span>
                  <div>
                    <strong>Focus on the affected area</strong>
                    <p>Show damaged leaves, stems, fruit or other symptoms.</p>
                  </div>
                </div>

                <div className="clinic-instruction-item">
                  <span>03</span>
                  <div>
                    <strong>Keep the image sharp</strong>
                    <p>Avoid blurry, distant or heavily overexposed photos.</p>
                  </div>
                </div>

                <div className="clinic-instruction-item">
                  <span>04</span>
                  <div>
                    <strong>Keep it under 5 MB</strong>
                    <p>Smaller, clear images are faster to process.</p>
                  </div>
                </div>
              </div>

              <div className="clinic-example-grid">
                <div className="clinic-example-card">
                  <img
                    src="/Images/goodImage.webp"
                    alt="Good quality crop photo"
                  />
                  <div>
                    <span>GOOD</span>
                    <strong>Clear & focused</strong>
                  </div>
                </div>

                <div className="clinic-example-card">
                  <img
                    src="/Images/blurrImage.webp"
                    alt="Poor quality crop photo"
                  />
                  <div>
                    <span>AVOID</span>
                    <strong>Blurry or overexposed</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {disease && (
          <section className="clinic-result-section">
            <div className="clinic-section-heading">
              <span className="clinic-eyebrow">ANALYSIS RESULT</span>
              <h2>Your crop health report</h2>
              <p>
                Review the identified issue and the recommended precautions
                before taking the next step.
              </p>
            </div>

            <div className="clinic-result-grid">
              <div className="clinic-diagnosis-card">
                <div className="clinic-result-label">
                  <span>DIAGNOSIS</span>
                  <strong>01</strong>
                </div>
                <h3>{disease}</h3>
              </div>

              {precautions && (
                <div className="clinic-precautions-card">
                  <div className="clinic-result-label">
                    <span>RECOMMENDED ACTION</span>
                    <strong>02</strong>
                  </div>
                  <div className="clinic-precautions-content">
                    {precautions}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {recommendedProducts.length > 0 && (
          <section className="clinic-products-section">
            <div className="clinic-section-heading">
              <span className="clinic-eyebrow">FARM INPUTS</span>
              <h2>Recommended products</h2>
              <p>
                Products suggested for the diagnosed crop condition. Review the
                product details before purchasing.
              </p>
            </div>

            <div className="clinic-products-grid">
              {recommendedProducts.map((product) => (
                <Card key={product.id} className="clinic-product-card">
                  <div className="clinic-product-image-wrap">
                    <img
                      src={`${BASE_URL}/media/${product.product_image}`}
                      alt={product.product_name}
                      className="clinic-product-image"
                    />
                  </div>

                  <CardContent className="clinic-product-content">
                    <span className="clinic-product-kicker">
                      RECOMMENDED INPUT
                    </span>
                    <Typography variant="h6" className="clinic-product-name">
                      {product.product_name}
                    </Typography>

                    <Typography
                      variant="body2"
                      className="clinic-product-description"
                    >
                      {product.product_description}
                    </Typography>

                    <Button
                      variant="contained"
                      href={product.product_link}
                      target="_blank"
                      rel="noreferrer"
                      className="clinic-product-link"
                    >
                      View Product
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ className: "clinic-upload-dialog" }}
      >
        <DialogTitle className="clinic-dialog-title">
          <span>PHOTO UPLOAD</span>
          <strong>How would you like to add your photo?</strong>
        </DialogTitle>

        <DialogContent className="clinic-dialog-content">
          <div className="clinic-dialog-options">
            <Button
              variant="contained"
              component="label"
              startIcon={<PhotoCamera />}
              className="clinic-dialog-button"
            >
              Take Photo
              <input
                hidden
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
              />
            </Button>

            <Button
              variant="outlined"
              component="label"
              startIcon={<Upload />}
              className="clinic-dialog-button secondary"
            >
              Upload from Gallery
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
              />
            </Button>
          </div>
        </DialogContent>

        <DialogActions className="clinic-dialog-actions">
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CropClinic;
